import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactFormElevated';
import ParallaxBackground from '../components/ParallaxBackground';
import { FiPhone, FiCheckCircle, FiHome, FiUsers, FiFileText, FiClock, FiShield, FiStar, FiArrowRight, FiTool, FiKey, FiBriefcase, FiTrello } from 'react-icons/fi';

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  const stats = [
    { value: '1000+', label: 'Successful Closings', icon: FiShield },
    { value: '15+', label: 'Years Experience', icon: FiStar },
    { value: '100%', label: 'Satisfaction Rate', icon: FiUsers },
    { value: '24/7', label: 'Support Available', icon: FiClock },
  ];

  const services = [
    {
      title: 'Residential Buyers',
      description: 'First-time or experienced, we guide homebuyers through every step of the closing process.',
      icon: FiHome,
      gradient: 'from-blue-600 to-blue-700',
      link: '/',
    },
    {
      title: 'Residential Sellers',
      description: 'Smooth closing process for sellers, ensuring all documents are in order for a successful sale.',
      icon: FiKey,
      gradient: 'from-sky-500 to-blue-600',
      link: '/residential-sellers',
    },
    {
      title: 'New Construction',
      description: 'Specialized closing services for newly constructed homes and developments.',
      icon: FiTool,
      gradient: 'from-blue-700 to-indigo-700',
      link: '/new-construction',
    },
    {
      title: 'Commercial RE',
      description: 'Expert closing services for commercial real estate transactions of all sizes.',
      icon: FiBriefcase,
      gradient: 'from-sky-600 to-cyan-600',
      link: '/commercial-real-estate',
    },
    {
      title: 'Agents & Lenders',
      description: 'Partnership opportunities for real estate professionals seeking reliable closing services.',
      icon: FiUsers,
      gradient: 'from-blue-500 to-sky-500',
      link: '/agents-lenders',
    },
    {
      title: 'Deed Transfers',
      description: 'Efficient deed preparation and transfer services for property transactions.',
      icon: FiFileText,
      gradient: 'from-indigo-600 to-blue-700',
      link: '/deed-transfers',
    },
  ];

  const process = [
    { step: '01', title: 'Consultation', description: 'We discuss your closing needs and timeline' },
    { step: '02', title: 'Documentation', description: 'We prepare and review all necessary documents' },
    { step: '03', title: 'Coordination', description: 'We coordinate with all parties involved' },
    { step: '04', title: 'Closing', description: 'We facilitate a smooth closing day experience' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section with Parallax */}
      <ParallaxBackground
        image="/images/boathouse-row.jpg"
        className="min-h-screen flex items-center"
        overlay={true}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.25, 0.4, 0.25, 1] }}
            className="text-center"
          >
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full mb-8 border border-eagle-sky/30 shadow-2xl"
            >
              <FiStar className="h-5 w-5 text-eagle-sky" />
              <span className="text-white text-sm font-medium tracking-wide uppercase">
                Philadelphia's Trusted Closing Services
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-white leading-tight"
            >
              Eagle Closing Services
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="font-sans text-xl md:text-2xl text-eagle-sky mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              Professional closing services for buyers, sellers, agents, and lenders.
              <br className="hidden md:block" />
              Making your real estate transactions seamless and stress-free.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-5 justify-center items-center"
            >
              <a
                href="tel:4845808865"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-eagle-sky to-eagle-light text-white px-10 py-5 rounded-full font-semibold text-lg shadow-2xl hover:shadow-eagle-sky/50 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <FiPhone className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/80">Call Us Today</div>
                  <div className="font-bold">484-580-8865</div>
                </div>
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300"
              >
                <span>Get a Quote</span>
                <FiArrowRight className="h-5 w-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ delay: 1.5, duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-eagle-sky rounded-full" />
          </div>
        </motion.div>
      </ParallaxBackground>

      {/* Statistics Section - Floating Card */}
      <section className="relative -mt-20 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 md:p-12"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-eagle-blue to-eagle-dark rounded-2xl mb-4 shadow-lg">
                    <stat.icon className="h-8 w-8 text-eagle-sky" />
                  </div>
                  <div className="font-display text-3xl md:text-4xl font-bold text-eagle-deep mb-2">
                    {stat.value}
                  </div>
                  <div className="font-sans text-sm text-eagle-gray">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-eagle-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-eagle-blue/10 rounded-full mb-6">
              <span className="text-eagle-blue font-semibold text-sm uppercase tracking-wide">
                Our Services
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-eagle-deep mb-6">
              Closing Solutions for Everyone
            </h2>
            <p className="font-sans text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Whether you're buying, selling, or working in real estate, we provide specialized closing services tailored to your needs.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <Link key={index} to={service.link}>
                <motion.div
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                  className="group relative bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden h-full"
                >
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

                  {/* Icon */}
                  <div className={`relative z-10 w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="h-8 w-8 text-white" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="font-display text-xl font-bold text-eagle-deep mb-3 group-hover:text-eagle-blue transition-colors">
                      {service.title}
                    </h3>
                    <p className="font-sans text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-eagle-blue font-semibold group-hover:translate-x-1 transition-all">
                      Learn More <FiArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-eagle-blue rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-eagle-sky rounded-full filter blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-eagle-sky/10 rounded-full mb-6">
              <span className="text-eagle-sky font-semibold text-sm uppercase tracking-wide">
                Our Process
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-eagle-deep mb-6">
              How We Work
            </h2>
            <p className="font-sans text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A simple, streamlined process designed to make your closing experience smooth and stress-free.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center relative"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-eagle-blue to-eagle-dark rounded-2xl mb-6 shadow-lg text-eagle-sky font-display text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-display text-xl font-bold text-eagle-deep mb-3">
                  {item.title}
                </h3>
                <p className="font-sans text-gray-600">
                  {item.description}
                </p>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-eagle-sky to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-eagle-dark via-eagle-blue to-eagle-deep relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-eagle-sky rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-eagle-coral rounded-full filter blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience a Smooth Closing?
            </h2>
            <p className="font-sans text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let Eagle Closing handle the details so you can focus on what matters most.
              Contact us today to get started.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <motion.a
                href="tel:4845808865"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-eagle-sky to-eagle-light text-white px-10 py-5 rounded-full font-semibold text-lg shadow-2xl"
              >
                <FiPhone className="h-5 w-5" />
                <span>484-580-8865</span>
              </motion.a>

              <Link
                to="/#contact"
                className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                <span>Send Us a Message</span>
                <FiArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form with Parallax */}
      <ContactForm backgroundImage="/images/benjamin-franklin-bridge.jpg" />
    </motion.div>
  );
};

export default Home;
